import React from 'react';

export type DraggablePlace = 'center' | 'leftBottom';

interface DraggableProps {
	isDraggable?: boolean;
	isInOrigin?: boolean;
	className?: string;
	style?: React.CSSProperties;
	ignoreElements?: Array<string>;
	padding?: number;
	defaultPosition?: DraggablePlace;
	withoutTransform?: boolean;
	borderColor?: string;
	dragClassName?: string;
}

interface DraggableData {
	isDragging: boolean;
	shiftX?: number;
	shiftY?: number;
	posX?: number;
	posY?: number;
}

const resolvePosition = ({
	posX,
	posY,
	width,
	height,
	withoutTransform,
	padding,
}: {
	posX: number,
	posY: number,
	withoutTransform?: boolean,
	width: number,
	height: number,
	padding: number,
}) => {
	if (withoutTransform) {
		if (posY < padding) {
			posY = padding;
		}

		if (posX < padding) {
			posX = padding;
		}

		if (posX + width + padding > window.innerWidth
			&& window.innerWidth > width + 2 * padding) {
			posX = window.innerWidth - padding - width;
		}

		if (posY + height + padding > window.innerHeight
			&& window.innerHeight > height + 2 * padding) {
			posY = window.innerHeight - padding - height;
		}
	} else {
		if (posY < padding + height / 2) {
			posY = padding + height / 2;
		}

		if (posX < padding + width / 2) {
			posX = padding + width / 2;
		}

		if (posX + width / 2 + padding > window.innerWidth
			&& window.innerWidth > width + 2 * padding) {
			posX = window.innerWidth - padding - width / 2;
		}

		if (posY + height / 2 + padding > window.innerHeight
			&& window.innerHeight > height + 2 * padding) {
			posY = window.innerHeight - padding - height / 2;
		}
	}
	return { posY, posX };
};

const Draggable: React.FC<DraggableProps> = ({
	isDraggable, isInOrigin, className, style, children, ignoreElements, padding = 5, borderColor = 'green', dragClassName, ...rest
}) => {
	const { defaultPosition = 'center', withoutTransform = false } = rest;
	const [position, setPosition] = React.useState<{x: string, y: string}>(() => {
		if (defaultPosition === 'center') {
			return { x: '50%', y: '50%' };
		}
		return { x: 'unset', y: 'unset' };
	});
	const [defaultStyle, setDefaultStyle] = React.useState<React.CSSProperties>(() => {
		if (defaultPosition === 'center') {
			return {};
		}
		return {
			left: 'unset',
			right: `${padding}px`,
			top: 'unset',
			bottom: `${padding}px`,
			transform: undefined,
		};
	});

	const draggableRef = React.useRef<HTMLDivElement>(null);
	const draggableData = React.useRef<DraggableData>({ isDragging: false });

	const handleDragStart: React.PointerEventHandler = (e) => {
		if (!draggableRef.current || !isDraggable) return;
		const el = (e.target as any);

		const ignore = ignoreElements?.some((item) => {
			return el.classList.contains(item) || !!el.closest(`.${item}`);
		});

		if (ignore) return;

		draggableData.current.isDragging = true;
		e.currentTarget?.setPointerCapture(e.pointerId);
		const {
			width, height, left, top,
		} = e.currentTarget.getBoundingClientRect();

		if (withoutTransform) {
			draggableData.current.shiftX = e.clientX - left;
			draggableData.current.shiftY = e.clientY - top;
		} else {
			draggableData.current.shiftX = e.clientX - left - width / 2;
			draggableData.current.shiftY = e.clientY - top - height / 2;
		}
	};

	const handleDrag: React.PointerEventHandler = (e) => {
		if (!draggableRef.current || !draggableData.current.isDragging || !isDraggable) return;
		e.preventDefault();

		const { posX, posY } = resolvePosition({
			padding,
			withoutTransform,
			width: draggableRef.current.offsetWidth,
			height: draggableRef.current.offsetHeight,
			posY: e.clientY - (draggableData.current.shiftY || 0),
			posX: e.clientX - (draggableData.current.shiftX || 0),
		});

		draggableRef.current.style.right = 'unset';
		draggableRef.current.style.bottom = 'unset';

		if (!withoutTransform) {
			draggableRef.current.style.transform = 'translate(-50%, -50%)';
		}

		draggableData.current.posX = posX;
		draggableRef.current.style.left = `${posX}px`;

		draggableData.current.posY = posY;
		draggableRef.current.style.top = `${posY}px`;
		draggableRef.current.style.borderColor = borderColor;
		dragClassName && draggableRef.current.classList.add(dragClassName);
	};

	const handleDragEnd: React.PointerEventHandler = (e) => {
		if (!draggableRef.current || !draggableData.current.isDragging) return;
		e.preventDefault();

		draggableData.current.isDragging = false;
		draggableRef.current.style.borderColor = 'transparent';
		dragClassName && draggableRef.current.classList.remove(dragClassName);

		if (typeof draggableData.current.posX === 'number') {
			setPosition({
				x: `${draggableData.current.posX}px`,
				y: `${draggableData.current.posY}px`,
			});
			setDefaultStyle({});
		}
	};

	React.useEffect(() => {
		if (withoutTransform && defaultPosition === 'center' && draggableRef.current && !isInOrigin) {
			const width = draggableRef.current.offsetWidth;
			const height = draggableRef.current.offsetHeight;

			setPosition({
				x: `calc(50% - ${width / 2}px)`,
				y: `calc(50% - ${height / 2}px)`,
			});
		}
	}, [defaultPosition, isInOrigin, withoutTransform]);

	React.useEffect(() => {
		const resize = () => {
			setPosition((prev) => {
				if (!prev.x.includes('px') || !draggableRef.current) {
					return prev;
				}

				const { posX, posY } = resolvePosition({
					padding,
					withoutTransform,
					width: draggableRef.current.offsetWidth,
					height: draggableRef.current.offsetHeight,
					posY: parseInt(prev.y, 10),
					posX: parseInt(prev.x, 10),
				});
				return {
					x: `${posX}px`,
					y: `${posY}px`,
				};
			});
		};

		window.addEventListener('resize', resize);
		return () => {
			window.removeEventListener('resize', resize);
		};
	}, []);

	return (
		<div
			ref={draggableRef}
			className={className}
			style={{
				...style,
				position: 'fixed',
				left: !isInOrigin ? position.x : '50%',
				top: !isInOrigin ? position.y : '50%',
				border: '2px solid transparent',
				transform: withoutTransform ? undefined : 'translate(-50%, -50%)',
				...defaultStyle,
			}}
			onPointerDown={handleDragStart}
			onPointerUp={handleDragEnd}
			onPointerMove={handleDrag}
			onPointerLeave={handleDragEnd}
		>
			{children}
		</div>
	);
};

export default Draggable;
